// @spec GHG-STR-017
'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useGHGSettings, useUpdateGHGSettings } from '@/features/ghg/api/use-ghg-settings';
import type { Scope1CoverageKey } from '@/features/ghg/types/ghg-settings';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ghg/settings/scope1')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.scope1Settings'),
  }),
  component: Scope1SettingsPage,
});

function Scope1SettingsPage() {
  const { t } = useTranslation('ghg');
  const { settings, isLoading } = useGHGSettings();
  const { mutate, isPending } = useUpdateGHGSettings();
  const [assumptions, setAssumptions] = useState('');
  const [comments, setComments] = useState('');

  useEffect(() => {
    if (settings?.scope1Notes) {
      setAssumptions(settings.scope1Notes.assumptions);
      setComments(settings.scope1Notes.comments);
    }
  }, [settings?.scope1Notes]);

  const handleToggle = (key: Scope1CoverageKey, checked: boolean) => {
    mutate({ scope1Coverage: { [key]: { enabled: checked } } });
  };

  const handleSave = () => {
    mutate(
      { scope1Notes: { assumptions, comments } },
      { onSuccess: () => toast.success(t('toast.scope1SettingsSaved')) }
    );
  };

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-3xl space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-4 w-96" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    );
  }

  const coverage = settings?.scope1Coverage;

  return (
    <div className="p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <header>
          <h1 className="text-3xl font-semibold text-foreground">{t('settingsPage.scope1Heading')}</h1>
          <p className="mt-2 text-muted-foreground">
            {t('settingsPage.scope1Description')}
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>{t('settingsPage.scope1Coverage')}</CardTitle>
            <CardDescription>
              {t('settingsPage.scope1CoverageDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {([
                {
                  key: 'stationaryCombustion' as const,
                  label: t('settingsPage.stationaryCombustion'),
                  description: t('settingsPage.stationaryCombustionDesc'),
                },
                {
                  key: 'mobileCombustion' as const,
                  label: t('settingsPage.mobileCombustion'),
                  description: t('settingsPage.mobileCombustionDesc'),
                },
                {
                  key: 'fugitiveEmissions' as const,
                  label: t('settingsPage.fugitiveEmissions'),
                  description: t('settingsPage.fugitiveEmissionsDesc'),
                },
                {
                  key: 'processEmissions' as const,
                  label: t('settingsPage.processEmissions'),
                  description: t('settingsPage.processEmissionsDesc'),
                },
              ]).map((item) => (
                <div
                  key={item.key}
                  className="flex items-start justify-between gap-4 rounded-lg border p-3"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`scope1-${item.key}`}
                        checked={coverage?.[item.key]?.enabled ?? true}
                        onCheckedChange={(checked) => {
                          handleToggle(item.key, checked);
                        }}
                      />
                      <Label htmlFor={`scope1-${item.key}`} className="cursor-pointer font-medium">
                        {item.label}
                      </Label>
                    </div>
                    <p className="text-xs text-muted-foreground">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('settingsPage.assumptions')}</CardTitle>
            <CardDescription>
              {t('settingsPage.scope1AssumptionsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={assumptions}
              onChange={(event) => {
                setAssumptions(event.target.value);
              }}
              placeholder={t('settingsPage.scope1AssumptionsPlaceholder')}
              rows={6}
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('settingsPage.additionalComments')}</CardTitle>
            <CardDescription>
              {t('settingsPage.scope1CommentsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={comments}
              onChange={(event) => {
                setComments(event.target.value);
              }}
              placeholder={t('settingsPage.commentsPlaceholder')}
              rows={6}
            />
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={isPending}>
            {t('settingsPage.saveSettings')}
          </Button>
        </div>
      </div>
    </div>
  );
}
